import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';
import { useLinksource } from '@/hooks/useLinksource/useLinksource';
import { useScrollToElementInHash } from '@/hooks/useScrollToElementInHash';

import styles from './OnlineScheduling.module.scss';

const OnlineScheduling = ({
    title,
    subTitle,
    body,
    cta,
    simpleWidgetAPIKey,
    scriptSource,
    embeddedWidgetAPIKey,
    departmentId,
    visitTypeId,
    providerId,
    onlineBookingAvailable,
    acceptingNewPatients,
    url,
    id,
    htmlId,
    iframeTitle = 'View available dates and times for appointments.',
    ...props
}) => {
    const scriptId = 'scheduling-widget-script';
    const containerId = id;
    const elRef = useRef(null);
    const schedulingRef = useRef(null);
    const scriptRef = useRef(null);
    const isWidgetRendered = useRef(false);
    const [iframehasLoaded, setIframehasLoaded] = useState(false);
    const [intervalId, setIntervalId] = useState(null);
    const { linksource, isLinksourceReady } = useLinksource({
        url,
        willUpdate: false,
        withLinksource: false,
    });

    useScrollToElementInHash(schedulingRef, iframehasLoaded);

    const createWidget = useCallback(() => {
        if (!acceptingNewPatients || !window?.mychartWidget || isWidgetRendered.current) {
            return;
        }

        window.mychartWidget.renderWidget({
            url,
            apiKey: embeddedWidgetAPIKey,
            widgetType: 'openscheduling',
            containerId,
            additionalParams: {
                dept: departmentId,
                vt: visitTypeId,
                id: providerId,
                linksource,
            },
        });

        isWidgetRendered.current = true;
    }, [
        acceptingNewPatients,
        embeddedWidgetAPIKey,
        containerId,
        departmentId,
        visitTypeId,
        providerId,
        url,
        linksource,
    ]);

    // On mount fetch the script and create the widget
    // If the script is already loaded, create the widget
    // If the script is not loaded, load the script and create the widget
    useEffect(() => {
        if (scriptRef.current || !onlineBookingAvailable || !isLinksourceReady) {
            return;
        } else if (document.getElementById(scriptId) && !isWidgetRendered.current) {
            createWidget();
            return;
        }

        const script = document.createElement('script');
        script.src = scriptSource;
        script.async = true;
        script.id = scriptId;
        script.onload = createWidget;
        scriptRef.current = script;

        document.body.appendChild(script);
    }, [scriptRef, elRef, scriptSource, createWidget, onlineBookingAvailable, isLinksourceReady]);

    // The widget does not have an option to include a title to the iframe
    // Adding the iframe title by DOM manipulation when the iframe has been rendered
    useEffect(() => {
        if (!intervalId && elRef && elRef.current && !iframehasLoaded) {
            setIntervalId(
                setInterval(() => {
                    const frame = elRef.current?.querySelector('iframe') || null;

                    if (frame) {
                        setIframehasLoaded(true);
                        frame.title = iframeTitle;
                    } else {
                        clearInterval(intervalId);
                    }
                }, 100),
            );
        }
    }, [iframehasLoaded, elRef, intervalId, iframeTitle]);

    // Clearing the interval once the title is added
    useEffect(() => {
        if (iframehasLoaded) {
            clearInterval(intervalId);
        }
    }, [iframehasLoaded, intervalId]);

    if (!onlineBookingAvailable) {
        return null;
    }

    return (
        <ContentContainer
            ref={schedulingRef}
            id={htmlId}
            className={styles[`online-scheduling`]}
            {...props}
        >
            {title && (
                <Title
                    className={styles[`online-scheduling__title`]}
                    content={title}
                    variant={Title.VARIANT.H3}
                    tag={Title.TAG.H2}
                />
            )}
            <div className={styles[`online-scheduling__content`]}>
                {subTitle && (
                    <Text
                        className={styles[`online-scheduling__sub-heading`]}
                        content={subTitle}
                        variant={Text.VARIANT.E1}
                        tag={Text.TAG.P}
                    />
                )}

                {body && (
                    <RichTextEditor
                        className={styles[`online-scheduling__body`]}
                        text={body}
                        typeT2
                    />
                )}

                {cta && cta?.href && cta?.label && (
                    <Button
                        className={styles[`online-scheduling__cta`]}
                        {...cta}
                        buttonStyle={Button.STYLE.SECONDARY}
                        analytics={{
                            componentName: 'fad_bio_scheduling_widget',
                            componentTitle: title,
                            interactionType: 'fad_bio_cta',
                        }}
                    />
                )}
            </div>

            {acceptingNewPatients && (
                <div className={styles[`online-scheduling__iframe`]} id={containerId} ref={elRef} />
            )}
        </ContentContainer>
    );
};

OnlineScheduling.propTypes = {
    /**
     * The title of the section.
     */
    title: PropTypes.string,
    /**
     * The subTitle of the section.
     */
    subTitle: PropTypes.string,
    /**
     * The body of the section.
     */
    body: RichTextEditor.propTypes.content,
    /**
     * The call to actions of the section.
     */
    cta: Button.propTypes,
    /**
     * The simple widget API key.
     */
    simpleWidgetAPIKey: PropTypes.string,
    /**
     * The script source.
     */
    scriptSource: PropTypes.string,
    /**
     * The embedded widget API key.
     */
    embeddedWidgetAPIKey: PropTypes.string,
    /**
     * The department ID.
     */
    departmentId: PropTypes.string,
    /**
     * The visit type ID.
     */
    visitTypeId: PropTypes.string,
    /**
     * The provider ID.
     */
    providerId: PropTypes.string,
    /**
     * If online booking is available.
     */
    onlineBookingAvailable: PropTypes.bool,
    /**
     * If accepting new patients.
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Iframe title
     */
    iframeTitle: PropTypes.string,
};

export default OnlineScheduling;
